import {
  CopyOutlined,
  GiftOutlined,
  LoginOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { Badge, Button, Drawer, Dropdown, Layout, Menu, Modal, Space, Typography, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import apiClient from '../utils/axiosConfig';
import Cart from './Cart';
import UserAuth from './UserAuth';

const { Header } = Layout;
const { Title } = Typography;

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authVisible, setAuthVisible] = useState(false);
  const [invitationCode, setInvitationCode] = useState<string>('');
  const [invitationModalVisible, setInvitationModalVisible] = useState(false);
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [couponData, setCouponData] = useState<any>(null);
  const location = useLocation();
  const { t } = useI18n();
  const { isAuthenticated, user, logout } = useAuth();
  const { state } = useCart();
  // 添加响应式样式
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* 防止页面水平滚动和右侧白边 */
      * {
        box-sizing: border-box;
      }
      
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        overflow-x: hidden;
      }
      
      #root {
        width: 100%;
        overflow-x: hidden;
      }
      
      .ant-layout {
        width: 100%;
        overflow-x: hidden;
      }
      
      .ant-layout-content {
        width: 100%;
        overflow-x: hidden;
      }
      
      /* 确保容器不会超出视口宽度 */
      .container {
        max-width: 100%;
        padding-left: 15px;
        padding-right: 15px;
        margin-left: auto;
        margin-right: auto;
      }
      
      /* 确保所有页面内容不超出视口 */
      .home-page,
      .about-page,
      .products-page,
      .symptoms-page,
      .shopping-guide-page,
      .classroom-page,
      .saline-guide-page {
        width: 100%;
        overflow-x: hidden;
      }
      
      /* 确保主页hero section紧贴导航栏 */
      .home-page .hero-section {
        margin-top: 0 !important;
        padding-top: 64px;
      }
      
      /* 确保主页内容紧贴导航栏 */
      .home-page {
        margin-top: 0 !important;
      }
      
      /* 为所有页面内容添加顶部间距，避免被固定导航栏遮挡 */
      .coupon-management,
      .logistics-tracking,
      .product-catalog,
      .customer-service,
      .shopping-guide-page,
      .classroom-page,
      .saline-guide-page,
      .symptoms-page,
      .about-page {
      }
      
      /* 移动端调整 */
      @media (max-width: 768px) {
        .coupon-management,
        .logistics-tracking,
        .product-catalog,
        .customer-service,
        .shopping-guide-page,
        .classroom-page,
        .saline-guide-page,
        .symptoms-page,
        .about-page {
        }
      }
      
      @media (max-width: 768px) {
        .mobile-menu-btn {
          display: inline-block !important;
        }
        .desktop-menu {
          display: none !important;
        }
        .desktop-actions {
          display: none !important;
        }
      }
      @media (min-width: 769px) {
        .mobile-menu-btn {
          display: none !important;
        }
        .desktop-menu {
          display: flex !important;
        }
        .desktop-actions {
          display: flex !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 当用户登录时获取优惠券数据
  useEffect(() => {
    if (isAuthenticated && !couponData) {
      fetchCouponData();
    }
  }, [isAuthenticated, couponData]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // 关闭移动菜单
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // 获取邀请码
  const fetchInvitationCode = async () => {
    try {
      const response = await apiClient.get('/auth/invitation-code');
      setInvitationCode(response.data.invitationCode);
    } catch (error) {
      console.error('获取邀请码失败:', error);
      message.error(t('messages.invitation.fetchFailed'));
    }
  };

  // 显示邀请码
  const showInvitationCode = async () => {
    if (!invitationCode) {
      await fetchInvitationCode();
    }
    setInvitationModalVisible(true);
  };

  // 复制邀请码
  const copyInvitationCode = () => {
    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(invitationCode).then(() => {
        message.success(t('messages.invitation.invitationCodeCopied'));
      }).catch(() => {
        message.error(t('messages.invitation.copyFailed'));
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = invitationCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(t('messages.invitation.invitationCodeCopied'));
      } catch (err) {
        message.error(t('messages.invitation.copyFailed'));
      }
      document.body.removeChild(textArea);
    }
  };

  // 获取优惠券数据
  const fetchCouponData = async () => {
    try {
      const response = await apiClient.get('/coupons/total');
      setCouponData(response.data);
    } catch (error) {
      console.error('获取优惠券数据失败:', error);
      message.error(t('messages.coupon.fetchFailed'));
    }
  };



  // 用户菜单
  const userMenu = {
    items: [
      {
        key: 'email',
        label: user?.email || '',
        disabled: true,
        style: {
          color: '#333333',
          fontSize: '12px',
          cursor: 'default'
        }
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'orders',
        label: <Link to="/orders" onClick={closeMobileMenu}>{t('nav.myOrders')}</Link>,
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'invitation',
        label: t('nav.myInvitationCode'),
        icon: <GiftOutlined />,
        onClick: () => {
          showInvitationCode();
          closeMobileMenu();
        },
      },
      {
        key: 'coupons',
        label: t('nav.myCoupons'),
        icon: <GiftOutlined />,
        onClick: () => {
          window.location.href = '/coupons';
          closeMobileMenu();
        },
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        label: t('nav.logout'),
        onClick: () => {
          logout();
          closeMobileMenu();
        },
      },
    ],
  };

  const menuItems = [
    /* {
       key: '/',
       icon: <HomeOutlined />,
       label: <Link to="/" onClick={closeMobileMenu}>{t('nav.home')}</Link>,
     },*/
    {
      key: '/products',
      label: <Link to="/products" onClick={closeMobileMenu}>{t('nav.products')}</Link>,
    },
    {
      key: '/usage',
      label: <Link to="/usage" onClick={closeMobileMenu}>{t('nav.usage')}</Link>,
    },
    {
      key: '/symptoms',
      label: <Link to="/symptoms" onClick={closeMobileMenu}>{t('nav.symptoms')}</Link>,
    },
    {
      key: '/classroom',
      label: <Link to="/classroom" onClick={closeMobileMenu}>{t('nav.classroom')}</Link>,
    },
    {
      key: '/shopping-guide',
      label: <Link to="/shopping-guide" onClick={closeMobileMenu}>{t('nav.shoppingGuide')}</Link>,
    },
    {
      key: '/about',
      label: <Link to="/about" onClick={closeMobileMenu}>{t('nav.about')}</Link>,
    }
  ];

  return (
    <>
      <Header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
        height: '64px',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#9cd5e0',
        boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
        transition: 'all 0.3s ease',
        margin: 0
      }}>
        {/* Logo - Visible on all devices */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '64px',
          marginRight: '24px'
        }}>
          <Link to="/" style={{ color: '#17a2b8', textDecoration: 'none' }}>
            <img
              src="/img/logo.jpg"
              alt={t('nav.brand')}
              style={{
                height: '50px',
                width: 'auto',
                objectFit: 'contain',
                verticalAlign: 'middle'
              }}
            />
          </Link>
        </div>

        {/* Desktop Menu - Hidden on mobile */}
        <div className="desktop-menu" style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          justifyContent: 'center'
        }}>

          <Menu
            theme="light"
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={menuItems}
            style={{
              background: 'transparent',
              border: 'none',
              minWidth: '600px',
              justifyContent: 'center',
              fontSize: '16px'
            }}
          />
        </div>

        {/* Right Actions - Desktop */}
        <Space size="middle" className="desktop-actions">

          {isAuthenticated && (
            <Badge count={state.totalItems} offset={[-5, 5]}>
              <Button
                type="primary"
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  window.dispatchEvent(new Event('openCart'));
                }}
              >
                {t('nav.shoppingCart')}
              </Button>
            </Badge>
          )}
          {isAuthenticated ? (
            <Dropdown menu={userMenu} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />} style={{ color: '#17a2b8', fontSize: '16px' }}>
                {t('nav.myAccount')}
              </Button>
            </Dropdown>
          ) : (
            <Button
              type="text"
              icon={<LoginOutlined />}
              style={{ color: '#17a2b8', fontSize: '16px' }}
              onClick={() => setAuthVisible(true)}
            >
              {t('nav.login')}
            </Button>
          )}
        </Space>

        {/* Mobile Menu Button */}
        <Button
          type="text"
          icon={<MenuOutlined />}
          style={{
            color: '#17a2b8',
            fontSize: '18px',
            border: 'none',
            boxShadow: 'none'
          }}
          onClick={() => setIsMobileMenuOpen(true)}
          className="mobile-menu-btn"
        />
      </Header>

      {/* Mobile Drawer */}
      <Drawer
        title={'Menu'}
        placement="right"
        onClose={() => setIsMobileMenuOpen(false)}
        open={isMobileMenuOpen}
        width={280}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{ border: 'none', fontSize: '16px' }}
        />
        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0' }}>
          <Space direction="vertical" style={{ width: '100%' }}>

            {/* Shopping Cart Button for Mobile - Only show when logged in */}
            {isAuthenticated && (
              <Badge count={state.totalItems} style={{ width: '100%' }}>
                <Button
                  className="btn-secondary-unified btn-block-unified"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => {
                    // 触发打开购物车的自定义事件
                    window.dispatchEvent(new Event('openCart'));
                    setIsMobileMenuOpen(false);
                  }}
                  style={{ width: '100%' }}
                >
                  {t('nav.shoppingCart')}
                </Button>
              </Badge>
            )}

            {isAuthenticated ? (
              <div style={{ width: '100%' }}>
                <div style={{ 
                  padding: '12px 16px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <UserOutlined />
                  <span style={{ fontWeight: 500 }}>{t('nav.myAccount')}</span>
                </div>
                <Space direction="vertical" style={{ width: '100%', gap: '4px' }}>
                  <div style={{ fontSize: '12px', color: '#999', padding: '8px 16px' }}>
                    {user?.email || ''}
                  </div>
                  <Button
                    type="text"
                    icon={<FileTextOutlined />}
                    style={{ width: '100%', textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      window.location.href = '/orders';
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('nav.myOrders')}
                  </Button>
                  <Button
                    type="text"
                    icon={<GiftOutlined />}
                    style={{ width: '100%', textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      showInvitationCode();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('nav.myInvitationCode')}
                  </Button>
                  <Button
                    type="text"
                    icon={<GiftOutlined />}
                    style={{ width: '100%', textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      window.location.href = '/coupons';
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('nav.myCoupons')}
                  </Button>
                  <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
                  <Button
                    type="text"
                    danger
                    style={{ width: '100%', textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('nav.logout')}
                  </Button>
                </Space>
              </div>
            ) : (
              <Button
                className="btn-primary-unified btn-block-unified"
                icon={<LoginOutlined />}
                onClick={() => {
                  setAuthVisible(true);
                  setIsMobileMenuOpen(false);
                }}
              >
                {t('nav.login')}
              </Button>
            )}
          </Space>
        </div>
      </Drawer>

      <UserAuth
        visible={authVisible}
        onClose={() => setAuthVisible(false)}
      />

      {/* 邀请码模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GiftOutlined style={{ color: '#17a2b8' }} />
            {t('messages.invitation.myInvitationCode')}
          </div>
        }
        open={invitationModalVisible}
        onCancel={() => setInvitationModalVisible(false)}
        footer={[
          <Button
            key="copy"
            className="btn-primary-unified"
            icon={<CopyOutlined />}
            onClick={copyInvitationCode}
          >
            {t('messages.invitation.copyInvitationCode')}
          </Button>,
          <Button
            key="close"
            className="btn-outline-unified"
            onClick={() => setInvitationModalVisible(false)}
          >
            {t('messages.invitation.close')}
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#17a2b8',
            letterSpacing: '2px',
            marginBottom: '16px',
            padding: '16px',
            border: '2px dashed #d9d9d9',
            borderRadius: '8px',
            backgroundColor: '#fafafa'
          }}>
            {invitationCode}
          </div>
          <div style={{ color: '#333333', margin: 0, lineHeight: '1.6' }}>
            {t('messages.invitation.shareInvitationCode').split('\n').map((line: string, index: number) => (
              <div key={index} style={{ marginBottom: index === 0 ? '8px' : '4px' }}>
                {line}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* 优惠券信息模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GiftOutlined style={{ color: '#17a2b8' }} />
            {t('nav.myCoupons')}
          </div>
        }
        open={couponModalVisible}
        onCancel={() => setCouponModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setCouponModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {couponData?.success ? (
            <>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#17a2b8',
                marginBottom: '16px',
                padding: '20px',
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#f6ffed'
              }}>
                NT${couponData.data?.activeTotal || '0.00'}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '16px', color: '#333333', marginBottom: '8px' }}>
                  {t('messages.coupon.availableTotal')}
                </div>
                <div style={{ fontSize: '14px', color: '#999' }}>
                  {t('messages.coupon.totalAmount')}: NT${couponData.data?.totalAmount || '0.00'}
                </div>
              </div>
              <div style={{ color: '#333333', fontSize: '14px' }}>
                {t('messages.coupon.earnMethod')}
              </div>
            </>
          ) : (
            <div style={{ color: '#999', fontSize: '16px' }}>
              {t('messages.coupon.noData')}
            </div>
          )}
        </div>
      </Modal>

      {/* 购物车组件 - 供移动端和桌面端共用，仅在登录时显示 */}
      {isAuthenticated && <Cart onCloseMobileMenu={closeMobileMenu} />}
    </>
  );
};

export default Navbar;
